// Route sheet helpers: addresses, Google Maps links and totals for a list of
// self-delivery orders delivered in the order given.

import { productNote } from "./lineNoteText";

// The Maps key from the environment; anything after "&" (e.g. a stray
// "&libraries" kept for the JS map loaders) is not part of the key.
export const googleMapsKey = () =>
  String(process.env.REACT_APP_GOOGLE_KEY ?? "")
    .split("&")[0]
    .trim();

const clean = (value) => String(value ?? "").trim();

// A line's product name as the order view shows it. The order form saves a
// variant as "Product - Variant", and variant names often start with the
// product's name already ("Dumbbells - Dumbbells - 15lb"): the repeat goes.
export const productName = (item) => {
  const name = clean(item?.product_name) || clean(item?.products?.name);
  const repeated = name.match(/^(.+?) - \1 - (.+)$/);
  return repeated ? `${repeated[1]} - ${repeated[2]}` : name;
};
const isCanada = (country) =>
  !country || /^(ca|can|canada)$/i.test(clean(country));

const joinAddress = ({ street, city, state, postcode, country }) => {
  const region = [clean(state), clean(postcode).toUpperCase()]
    .filter(Boolean)
    .join(" ");
  return [
    clean(street),
    clean(city),
    region,
    isCanada(country) ? "" : clean(country),
  ]
    .filter(Boolean)
    .join(", ");
};

// "926 Ridge Rd, Anjou, QC H1K 1B7"; the country only when it isn't Canada
// (keeps the links short). Empty when there's no street or postal code to
// drive to.
export const deliveryAddress = (shipping) => {
  const street = [shipping?.address_1, shipping?.address_2]
    .map(clean)
    .filter(Boolean)
    .join(" ");
  if (!street && !clean(shipping?.postcode)) return "";
  return joinAddress({
    street,
    city: shipping?.city,
    state: shipping?.state || shipping?.state_name,
    postcode: shipping?.postcode,
    country: shipping?.country_name || shipping?.country,
  });
};

export const locationAddress = (warehouse) =>
  joinAddress({
    street: warehouse?.address,
    city: warehouse?.city,
    state: warehouse?.state,
    postcode: warehouse?.postcode,
    country: warehouse?.country,
  });

const query = (place) => encodeURIComponent(place).replace(/%20/g, "+");

// Google Maps directions link (Maps URLs). An empty origin means the
// phone's current location.
export const directionsUrl = ({ origin, destination, waypoints = [] }) =>
  "https://www.google.com/maps/dir/?api=1" +
  (origin ? `&origin=${query(origin)}` : "") +
  `&destination=${query(destination)}` +
  (waypoints.length ? `&waypoints=${waypoints.map(query).join("%7C")}` : "") +
  "&travelmode=driving";

// Turn-by-turn to one address, from wherever the phone is.
export const navigateUrl = (address) =>
  `${directionsUrl({ destination: address })}&dir_action=navigate`;

// A Maps link takes at most 9 stops between its two ends, so a longer run is
// split into links of even length: start → stops → back to the start.
// Without a start, the first link starts from the phone's location and the
// run ends at the last stop.
const MAX_WAYPOINTS = 9;
export const runLinks = (start, stops, startName = "the start") => {
  const n = stops.length;
  if (!n) return [];
  const points = start ? [start, ...stops, start] : [null, ...stops];
  const segments = points.length - 1;
  const per = Math.ceil(segments / Math.ceil(segments / (MAX_WAYPOINTS + 1)));
  const links = [];
  for (let i = 0; i < segments; i += per) {
    const j = Math.min(i + per, segments);
    const first = i + 1;
    const last = Math.min(j, n);
    const label =
      first > n
        ? `Back to ${startName}`
        : `Stops ${first === last ? first : `${first}–${last}`}${
            j === n + 1 ? " and back" : ""
          }`;
    links.push({
      label,
      url: directionsUrl({
        origin: points[i],
        destination: points[j],
        waypoints: points.slice(i + 1, j),
      }),
    });
  }
  return links;
};

// The embedded map (Maps Embed API, free) shows the road route with its
// distance and drive time; it takes at most 20 stops.
export const EMBED_MAX_STOPS = 20;
export const embedUrl = (key, start, stops) => {
  const shown = stops.slice(0, EMBED_MAX_STOPS);
  if (!key || !shown.length) return "";
  const origin = start || shown[0];
  const destination = start || shown[shown.length - 1];
  const waypoints = start ? shown : shown.slice(1, -1);
  return (
    "https://www.google.com/maps/embed/v1/directions" +
    `?key=${encodeURIComponent(key)}` +
    `&origin=${query(origin)}&destination=${query(destination)}` +
    (waypoints.length ? `&waypoints=${waypoints.map(query).join("|")}` : "") +
    "&mode=driving&units=metric"
  );
};

// The lines a delivery is about: the order's products from Bolt, as the
// order view's status pop-up fulfils them. Lines synced from a marketplace
// (plugin_item_id) repeat those products under the store's names, so they're
// left out, unless the order has no products from Bolt at all.
export const deliveryLines = (order) => {
  const lines = order?.order_details ?? [];
  const bolt = lines.filter((item) => !item?.plugin_item_id);
  return bolt.length ? bolt : lines;
};

// Pieces, and the products' weight in lb (weight × quantity per line, as the
// Items column counts it; a product measured in kg is converted); null unless
// every line has a weight.
export const orderPieces = (order) =>
  deliveryLines(order).reduce(
    (sum, item) => sum + Number(item?.item_quantity ?? 0),
    0,
  );

const KG_TO_LB = 2.20462;
// One piece's weight in lb: NaN when the line has none.
const pieceWeightLb = (item) => {
  const weight = parseFloat(item?.weight);
  return item?.product_length_type === "Metric" ? weight * KG_TO_LB : weight;
};

export const orderWeight = (order) => {
  const lines = deliveryLines(order);
  if (!lines.length) return null;
  const weights = lines.map(
    (item) => pieceWeightLb(item) * Number(item?.item_quantity ?? 0),
  );
  return weights.every(Number.isFinite)
    ? weights.reduce((sum, weight) => sum + weight, 0)
    : null;
};

// Packages: one a piece, unless the product's package details in Bolt list
// several boxes (a rack shipped in two boxes is two packages a piece), as
// the shipping page counts them.
export const orderPackages = (order) =>
  deliveryLines(order).reduce((sum, item) => {
    const boxes = Array.isArray(item?.bolt_package_detail)
      ? item.bolt_package_detail.length
      : 0;
    return sum + Number(item?.item_quantity ?? 0) * Math.max(1, boxes);
  }, 0);

export const formatLb = (lb) =>
  `${lb >= 10 ? Math.round(lb).toLocaleString("en-CA") : Math.round(lb * 10) / 10} lb`;

// What a list of orders adds up to: pieces, packages, and the weight of the
// pieces that have one (`unweighed` counts the pieces without).
export const loadTotals = (orders) => {
  const totals = { pieces: 0, packages: 0, weight: 0, unweighed: 0 };
  (orders ?? []).forEach((order) => {
    totals.pieces += orderPieces(order);
    totals.packages += orderPackages(order);
    deliveryLines(order).forEach((item) => {
      const quantity = Number(item?.item_quantity ?? 0);
      const weight = pieceWeightLb(item);
      if (Number.isFinite(weight)) totals.weight += weight * quantity;
      else totals.unweighed += quantity;
    });
  });
  return totals;
};
// "19 pcs · 21 packages · 410 lb". Pieces without a weight make it "over
// 410 lb"; none with a weight, no weight at all.
export const totalsParts = ({ pieces, packages, weight, unweighed }) => {
  const parts = [
    `${pieces} pcs`,
    `${packages} ${packages === 1 ? "package" : "packages"}`,
  ];
  if (weight > 0) {
    parts.push(unweighed > 0 ? `over ${formatLb(weight)}` : formatLb(weight));
  }
  return parts;
};
export const unweighedNote = ({ unweighed }) =>
  unweighed > 0
    ? `${unweighed} ${unweighed === 1 ? "piece has" : "pieces have"} no weight in Bolt`
    : "";

// Default stop order: by postal code, which keeps nearby addresses together;
// orders without an address last.
const postalKey = (order) =>
  clean(order?.order_shipping_address_details?.postcode)
    .replace(/\s/g, "")
    .toUpperCase();
export const byPostalCode = (a, b) => {
  const pa = deliveryAddress(a?.order_shipping_address_details)
    ? postalKey(a)
    : "￿";
  const pb = deliveryAddress(b?.order_shipping_address_details)
    ? postalKey(b)
    : "￿";
  return pa === pb ? Number(a?.id) - Number(b?.id) : pa < pb ? -1 : 1;
};

// Each item's fulfilment state, read the way the app reads it (the shared
// order items pop-up): a cancelled order cancels every line; Fulfilled (4)
// fulfils every line; Packing packs them; Attention (5) is per line —
// is_saleable "1" is fulfilled, otherwise what's left to pick is the ordered
// quantity less saleable_item_quantity; anything else is still to pick.
// Packing has no fixed id, so it's found by name in the app's status list.
export const FULFILLED_STATUS = 4;
export const ATTENTION_STATUS = 5;
export const CANCELLED_STATUS = 6;

const count = (value) => Math.max(0, Number(value ?? 0) || 0);

export const lineStates = (order, statuses = []) => {
  const statusId = Number(order?.order_status_id);
  const statusName =
    statuses.find((status) => Number(status?.id) === statusId)?.name ?? "";
  const cancelled =
    statusId === CANCELLED_STATUS ||
    clean(order?.status).toLowerCase() === "cancelled";

  return deliveryLines(order).map((item) => {
    const quantity = count(item?.item_quantity);
    const line = { item, quantity, fulfilled: 0, pending: 0 };
    if (cancelled) return { ...line, state: "cancelled" };
    if (statusId === FULFILLED_STATUS)
      return { ...line, state: "fulfilled", fulfilled: quantity };
    if (/^packing/i.test(clean(statusName)))
      return { ...line, state: "packing" };
    if (statusId === ATTENTION_STATUS) {
      const fulfilled = Math.min(quantity, count(item?.saleable_item_quantity));
      return String(item?.is_saleable) === "1" || fulfilled >= quantity
        ? { ...line, state: "fulfilled", fulfilled: quantity }
        : {
            ...line,
            state: "attention",
            fulfilled,
            pending: quantity - fulfilled,
          };
    }
    return { ...line, state: "pick", pending: quantity };
  });
};

// Every stop's lines with their state, and whether each is still open (to
// pick, or the pending part of an Attention line). An open line that isn't
// linked to a product is flagged "unlinked". There's no stock check: the
// product stock already has every open order taken off, and stock can sit
// off pallets, so neither number says what's on the shelf for this run.
const productIdOf = (item) => item?.products?.id ?? item?.item_id ?? null;

// The product's stock count at the order's location, as a rough guide: it
// already has every open order taken off, this one included. null when the
// order data carries no stock row for that location.
export const currentStock = (order, item) => {
  if (order?.warehouse_id == null) return null;
  const row = (item?.products?.product_warehouse ?? []).find(
    (stock) => String(stock?.warehouse_id) === String(order.warehouse_id),
  );
  const quantity = Number(row?.quantity);
  return row && row.quantity !== "" && Number.isFinite(quantity)
    ? quantity
    : null;
};

export const sheetLines = (stops, statuses = []) =>
  stops.map((order) =>
    lineStates(order, statuses).map((line) => {
      const open = line.state === "pick" || line.state === "attention";
      return {
        ...line,
        open,
        stock: currentStock(order, line.item),
        flag:
          open && line.pending && productIdOf(line.item) == null
            ? "unlinked"
            : null,
      };
    }),
  );

// The strip above the stop list: pieces still to pick, pieces being packed,
// lines already fulfilled, and lines not linked to a product.
export const sheetCounts = (lines) =>
  lines.flat().reduce(
    (counts, line) => {
      if (line.open) {
        counts.toPick += line.pending;
        if (line.flag === "unlinked") counts.unlinked += 1;
      } else if (line.state === "packing") counts.packing += line.quantity;
      else if (line.state === "fulfilled") counts.fulfilled += 1;
      return counts;
    },
    { toPick: 0, packing: 0, fulfilled: 0, unlinked: 0 },
  );

// Where to pick a product: its pallets at the order's location (the stock row
// the order data carries), with what each holds, in the order the app's
// Fulfilled pop-up would use them — the smallest pallet that holds the whole
// quantity first, then the rest, biggest first. Empty pallets are left out.
export const palletsFor = (item, quantity) => {
  const rows = new Set(
    (item?.products?.product_warehouse ?? []).map((row) => String(row?.id)),
  );
  const pallets = (item?.products?.product_pallets ?? [])
    .filter(
      (pallet) =>
        rows.has(String(pallet?.product_warehouse_id)) &&
        count(pallet?.quantity) > 0,
    )
    .map((pallet) => ({
      id: pallet.id,
      name:
        clean(pallet?.pallet?.name) ||
        clean(pallet?.pallet?.code) ||
        `Pallet #${pallet?.pallet_id}`,
      location:
        clean(pallet?.pallet?.location?.name) ||
        clean(pallet?.pallet?.location?.code),
      lot: clean(pallet?.product_lot?.name) || clean(pallet?.lot_number),
      quantity: count(pallet?.quantity),
    }));
  const first = pallets
    .filter((pallet) => pallet.quantity >= quantity)
    .sort((a, b) => a.quantity - b.quantity)[0];
  const rest = pallets
    .filter((pallet) => pallet !== first)
    .sort((a, b) => b.quantity - a.quantity);
  return first ? [first, ...rest] : rest;
};

// The pick list: the sheet's open lines added up product by product (one row
// per linked product, or per name when a line isn't linked), by name, each
// with the stops it's for (and the shipper's note on it for that order) and
// where to pick it; lines being packed and lines already fulfilled are
// listed apart, with their stop.
export const pickList = (stops, lines) => {
  const rows = new Map();
  const packing = [];
  const fulfilled = [];
  stops.forEach((order, index) =>
    (lines[index] ?? []).forEach((line) => {
      const stop = { number: index + 1, order, line };
      if (line.state === "fulfilled") return fulfilled.push(stop);
      if (line.state === "packing") return packing.push(stop);
      if (!line.open || !line.pending) return;
      const productId = productIdOf(line.item);
      const name = productName(line.item);
      const key =
        productId != null ? `p${productId}` : `n:${name.toLowerCase()}`;
      let row = rows.get(key);
      if (!row) {
        row = {
          key,
          item: line.item,
          name: name || "-",
          sku: clean(line.item?.sku) || clean(line.item?.products?.sku),
          unlinked: productId == null,
          quantity: 0,
          weight: 0,
          lineIds: [],
          stops: [],
        };
        rows.set(key, row);
      }
      const weight = pieceWeightLb(line.item) * line.pending;
      row.quantity += line.pending;
      row.weight = Number.isFinite(weight) ? row.weight + weight : NaN;
      row.lineIds.push(String(line.item?.id));
      row.stops.push({
        number: index + 1,
        orderId: order?.id,
        quantity: line.pending,
        attention: line.state === "attention",
        // The shipper's note on the product for this order, if any.
        note: productNote(line.item),
      });
      return undefined;
    }),
  );
  const toPick = [...rows.values()]
    .map(({ item, ...row }) => ({
      ...row,
      weight: Number.isFinite(row.weight) ? row.weight : null,
      pallets: row.unlinked ? [] : palletsFor(item, row.quantity),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return { toPick, packing, fulfilled };
};

// Picked ticks and Missing reports stay on this device, per order line, so
// the pick list keeps them when it's opened again; nothing goes to the
// server. The newest 1,000 of each are kept.
const PICKS_KEY = "orders:routeSheetPicks";
const KEEP = 1000;
export const readPicks = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(PICKS_KEY) || "{}");
    return {
      picked: new Set((stored.picked ?? []).map(String)),
      missing: new Set((stored.missing ?? []).map(String)),
    };
  } catch (error) {
    return { picked: new Set(), missing: new Set() };
  }
};
export const writePicks = ({ picked, missing }) => {
  try {
    localStorage.setItem(
      PICKS_KEY,
      JSON.stringify({
        picked: [...picked].slice(-KEEP),
        missing: [...missing].slice(-KEEP),
      }),
    );
  } catch (error) {
    // Storage can be off (private windows); the ticks then last until the
    // pick list closes.
  }
};

// Ticking a product as picked clears its Missing report, and the other way
// round; unticking only removes that mark.
export const togglePick = (picks, lineIds, mark) => {
  const other = mark === "picked" ? "missing" : "picked";
  const next = {
    picked: new Set(picks.picked),
    missing: new Set(picks.missing),
  };
  const on = lineIds.every((id) => next[mark].has(id));
  lineIds.forEach((id) => {
    if (on) next[mark].delete(id);
    else {
      next[mark].add(id);
      next[other].delete(id);
    }
  });
  return next;
};
